import { useCallback, useEffect, useState } from 'react';
import { permissions } from '@vanilla/services/PermissionService.js';
import { RevenueCalculationService } from '@vanilla/services/RevenueCalculationService.js';
import { DashboardService } from '@vanilla/services/DashboardService.js';
import { DashboardMetricsService as M } from '@vanilla/services/DashboardMetricsService.js';
import { DealService } from '@vanilla/services/DealService.js';
import { ActivityService } from '@vanilla/services/ActivityService.js';
import { auth } from '@vanilla/services/AuthService.js';
import { formatCurrencyShort } from '@vanilla/utils/Utils.js';
import {
    WIDGETS,
    WIDGET_REQUIRED_PERMISSION,
    FINANCIAL_WIDGETS,
} from '@vanilla/constants/DashboardConfig.js';
import { dashboardNeedsWarehouseFetch } from './dashboardKeys.js';

function canShowWidget(widgetId) {
    const requiredPermission = WIDGET_REQUIRED_PERMISSION?.[widgetId];
    if (requiredPermission) {
        const [mod, act] = requiredPermission.split(':');
        const allowed = mod === 'dashboard'
            ? permissions.canView('dashboard')
            : permissions.can(mod, act);
        if (!allowed) return false;
    }
    if (FINANCIAL_WIDGETS.includes(widgetId) && !permissions.canViewFinancials('dashboard')) {
        return false;
    }
    return true;
}

function money(v) {
    return permissions.financialText('dashboard', formatCurrencyShort(v || 0));
}

function dealLabel(d) {
    return d.dealName || d.name || 'Untitled deal';
}

function dealAmount(d) {
    return money(M.dealAmount(d));
}

function getDealsClosingSoon(deals, days = 7) {
    const now = Date.now();
    const limit = now + days * 86400000;
    return (deals || [])
        .filter((d) => M.isOpen(d) && M.expectedCloseDate(d))
        .filter((d) => {
            const t = new Date(String(M.expectedCloseDate(d)).slice(0, 10) + 'T00:00:00').getTime();
            return !Number.isNaN(t) && t >= now && t <= limit;
        })
        .slice(0, 5);
}

function getDealsRequiringAttention(deals) {
    return (deals || [])
        .filter((d) => M.isOpen(d))
        .filter((d) => {
            const close = M.expectedCloseDate(d) ? new Date(String(M.expectedCloseDate(d)).slice(0, 10) + 'T00:00:00').getTime() : null;
            return close != null && close < Date.now();
        })
        .slice(0, 5);
}

async function fetchDashboardData() {
    const year = new Date().getFullYear();
    const yearFilters = M.toScheduleFilters('this_year');
    const ownerId = auth.getSession()?.userId || null;
    const deals = await DealService.getListView();
    const [metricsRes, myMetricsRes, monthlyRes, goalRes, activitiesRes] = await Promise.allSettled([
        RevenueCalculationService.calculateMetrics(yearFilters),
        ownerId ? RevenueCalculationService.calculateMetrics({ ...yearFilters, ownerUserId: ownerId }) : Promise.resolve({ actualInPeriod: 0 }),
        RevenueCalculationService.generateMonthlyForecast(new Date(year, 0, 1), new Date(year, 11, 31)),
        DashboardService.getGoalMetrics(year),
        ActivityService.listRecent({ perPage: 50, activityType: 'task' }),
    ]);
    const requireValue = (res, fallback, label) => {
        if (res.status === 'fulfilled') return res.value;
        console.error(`[useDashboard] ${label} failed`, res.reason);
        return fallback;
    };
    const metrics = requireValue(metricsRes, null, 'company revenue');
    if (!metrics) throw new Error('Failed to load revenue metrics.');
    const myMetrics = requireValue(myMetricsRes, { actualInPeriod: 0 }, 'my revenue');
    const monthly = requireValue(monthlyRes, [], 'monthly forecast');
    const goalMetrics = requireValue(goalRes, { enriched: [], topPerformers: [], atRiskCount: 0 }, 'goals');
    const activities = requireValue(activitiesRes, [], 'tasks');
    const won = (deals || []).filter((d) => M.isWon(d));
    const pipeline = (deals || []).filter((d) => M.isOpen(d));
    const lost = (deals || []).filter((d) => M.isLost(d));
    const pipelineTotal = M.pipelineValue(deals);
    return {
        metrics,
        myMetrics,
        monthly: monthly || [],
        deals: deals || [],
        won,
        pipeline,
        lost,
        activities,
        pipelineTotal,
        goalMetrics: goalMetrics || { enriched: [], topPerformers: [], atRiskCount: 0 },
        year,
        ownerId,
    };
}

export function useDashboard() {
    const [config, setConfig] = useState({ title: 'Dashboard', subtitle: '', widgets: [] });
    const [widgets, setWidgets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [availableDashboards, setAvailableDashboards] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const [myDashboards, setMyDashboards] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const accessible = await DashboardService.getMyDashboards();
            setMyDashboards(accessible);
            const initial = accessible.find((d) => d.is_default) || accessible[0] || { label: 'Dashboard', title: 'Dashboard', subtitle: '', widgets: [] };
            setSelectedKey(initial.key || null);
            const dashboardConfig = {
                title: initial.label || initial.title || 'Dashboard',
                subtitle: initial.description || initial.subtitle || '',
                widgets: initial.widgets || [],
            };
            setConfig(dashboardConfig);
            let avail = null;
            if (permissions.can('dashboard', 'manage')) {
                const all = await DashboardService.getAllDashboards().catch(() => []);
                avail = all.length ? all : null;
            } else if (accessible.length > 1) {
                avail = accessible;
            } else {
                avail = null;
            }
            setAvailableDashboards(avail && avail.length ? avail : null);
            if (!dashboardNeedsWarehouseFetch(dashboardConfig, initial.key)) {
                setWidgets([]);
                return;
            }
            const data = await fetchDashboardData();
            const stringWidgets = (dashboardConfig.widgets || []).filter(w => typeof w === 'string');
            const built = stringWidgets
                .filter(canShowWidget)
                .map((id) => buildWidget(id, data))
                .filter(Boolean);
            setWidgets(built);
        } catch (err) {
            console.error('[useDashboard] failed', err);
            setError(err?.message || 'Failed to load dashboard data. Please try refreshing the page.');
            setWidgets([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const selectDashboard = useCallback(async (key) => {
        const source = availableDashboards?.length ? availableDashboards : myDashboards;
        const sel = source.find((d) => d.key === key) || myDashboards.find((d) => d.is_default) || source[0];
        if (!sel) return;
        setSelectedKey(sel.key);
        const nextConfig = {
            title: sel.label || sel.title || 'Dashboard',
            subtitle: sel.description || sel.subtitle || '',
            widgets: sel.widgets || [],
        };
        setConfig(nextConfig);
        try {
            if (!dashboardNeedsWarehouseFetch(nextConfig, sel.key)) {
                setWidgets([]);
                setError('');
                return;
            }
            const data = await fetchDashboardData();
            const stringWidgets = (nextConfig.widgets || []).filter(w => typeof w === 'string');
            const built = stringWidgets
                .filter(canShowWidget)
                .map((id) => buildWidget(id, data))
                .filter(Boolean);
            setWidgets(built);
            setError('');
        } catch (err) {
            console.error('[useDashboard] select failed', err);
            setError(err?.message || 'Failed to load dashboard data. Please try refreshing the page.');
            setWidgets([]);
        }
    }, [availableDashboards, myDashboards]);

    useEffect(() => {
        load();
    }, [load]);

    return { config, widgets, loading, error, refresh: load, availableDashboards, selectedKey, selectDashboard };
}

function listItems(rows, extras = {}) {
    if (!rows?.length) return { type: 'empty', text: 'Nothing to display', ...extras };
    return { type: 'list', items: rows, ...extras };
}

function pctBadgeClass(pct, ranked = false) {
    if (pct >= 100) return ranked ? 'rank-complete' : 'pct-completed';
    if (pct >= 70) return ranked ? 'rank-ontrack' : 'pct-on-track';
    return ranked ? 'rank-behind' : 'pct-behind';
}

function uniqueAccountNames(deals) {
    return [...new Set((deals || []).map((d) => d.clientName || d.accountName).filter(Boolean))];
}

function buildWidget(widgetId, data) {
    const { metrics, myMetrics, monthly, deals, won, pipeline, lost, activities, pipelineTotal, goalMetrics, year } = data;
    const winPct = M.winRate(deals);

    switch (widgetId) {
        case WIDGETS.COMPANY_REVENUE:
            return { id: widgetId, kind: 'kpi', title: 'Company Revenue', value: money(metrics.actualInPeriod), subtitle: 'Paid schedules this year' };
        case WIDGETS.PIPELINE_VALUE:
            return { id: widgetId, kind: 'kpi', title: 'Pipeline Value', value: money(pipelineTotal), subtitle: `${pipeline.length} open deals` };
        case WIDGETS.FORECAST: {
            const forecast = goalMetrics.enriched.find((g) => g.goalType === 'revenue');
            return { id: widgetId, kind: 'kpi', title: 'Forecast', value: money(forecast?.targetValue || 0), subtitle: 'Annual revenue target' };
        }
        case WIDGETS.CONVERSION_RATE:
            return { id: widgetId, kind: 'kpi', title: 'Win Rate', value: `${winPct}%`, subtitle: 'Won / (Won + Lost)' };
        case WIDGETS.AVERAGE_DEAL_SIZE:
            return { id: widgetId, kind: 'kpi', title: 'Average Deal Size', value: money(M.averageWonSize(deals)), subtitle: 'Average won deal value' };
        case WIDGETS.CLOSED_WON:
            return { id: widgetId, kind: 'kpi', title: 'Closed Won', value: money(M.closedWonValue(deals)), subtitle: `${won.length} deals` };
        case WIDGETS.MY_REVENUE:
            return { id: widgetId, kind: 'kpi', title: 'My Revenue', value: money(myMetrics?.actualInPeriod || 0), subtitle: 'Your paid schedules this year' };
        case WIDGETS.LEAD_CONVERSION:
            return { id: widgetId, kind: 'kpi', title: 'Lead Conversion', value: `${winPct}%`, subtitle: 'Won / (Won + Lost)' };
        case WIDGETS.REVENUE_TARGET: {
            const revenueGoal = goalMetrics.enriched.find((g) => g.goalType === 'revenue');
            const revenuePct = revenueGoal && revenueGoal.targetValue > 0
                ? Math.round((revenueGoal.achievedValue / revenueGoal.targetValue) * 100)
                : 0;
            return { id: widgetId, kind: 'progress', title: 'Revenue Target', value: revenuePct };
        }
        case WIDGETS.REVENUE_TREND:
        case WIDGETS.REVENUE_GROWTH:
        case WIDGETS.MONTHLY_GROWTH: {
            const isGrowthOnly = widgetId === WIDGETS.MONTHLY_GROWTH;
            return {
                id: widgetId,
                kind: 'chart',
                title: isGrowthOnly ? 'Monthly Growth' : (widgetId === WIDGETS.REVENUE_GROWTH ? 'Revenue Growth' : 'Revenue Trend'),
                rows: (monthly || []).slice(0, 12).map((m) => {
                    const planned = Number(m.planned) || 0;
                    const achieved = (Number(m.actual) || 0) + (Number(m.paid) || 0);
                    return {
                        label: m.monthLabel || m.label || m.month || '',
                        value: achieved,
                        ...(isGrowthOnly ? {} : { planned }),
                    };
                }),
                year,
            };
        }
        case WIDGETS.WON_VS_LOST:
            return {
                id: widgetId,
                kind: 'summary',
                title: 'Won vs Lost',
                stats: { Won: won.length, Lost: lost.length },
            };
        case WIDGETS.TOP_PERFORMERS:
        case WIDGETS.TEAM_PERFORMANCE: {
            const performers = (goalMetrics.topPerformers || []).slice(0, 5);
            return {
                id: widgetId,
                kind: 'list',
                title: widgetId === WIDGETS.TEAM_PERFORMANCE ? 'Team Performance' : 'Top Performers',
                ...listItems(performers.map((p) => {
                    const pct = p.progress || 0;
                    return {
                        primary: p.userName || p.name || '—',
                        metric: `${money(p.totalAchieved || p.achievedValue || p.revenue || 0)} / ${money(p.totalTarget || 0)}`,
                        badge: `${pct}%`,
                        badgeClass: pctBadgeClass(pct),
                    };
                })),
            };
        }
        case WIDGETS.TEAM_RANKING: {
            const performers = (goalMetrics.topPerformers || []).slice(0, 5);
            return {
                id: widgetId,
                kind: 'list',
                title: 'Team Ranking',
                ranked: true,
                ...listItems(performers.map((p, i) => {
                    const pct = p.progress || 0;
                    return {
                        primary: p.userName || p.name || '—',
                        rank: i + 1,
                        liClass: `rank-${i + 1}`,
                        badge: `${pct}%`,
                        badgeClass: pctBadgeClass(pct, true),
                    };
                }), { ranked: true }),
            };
        }
        case WIDGETS.MY_DEALS:
        case WIDGETS.OPEN_DEALS:
        case WIDGETS.RECENT_DEALS:
        case WIDGETS.BUSINESS_PIPELINE:
        case WIDGETS.QUALIFIED_OPPORTUNITIES: {
            const source = widgetId === WIDGETS.OPEN_DEALS || widgetId === WIDGETS.BUSINESS_PIPELINE
                ? pipeline
                : widgetId === WIDGETS.QUALIFIED_OPPORTUNITIES
                ? pipeline.filter((d) => {
                    const stage = M.dealStage(d);
                    return stage === 'qualified' || stage === 'qualification' || stage === 'proposal';
                })
                    : deals;
            return {
                id: widgetId,
                kind: 'list',
                title: widgetId === WIDGETS.MY_DEALS ? 'My Deals'
                    : widgetId === WIDGETS.OPEN_DEALS ? 'Open Deals'
                        : widgetId === WIDGETS.BUSINESS_PIPELINE ? 'Business Pipeline'
                            : widgetId === WIDGETS.QUALIFIED_OPPORTUNITIES ? 'Qualified Opportunities'
                                : 'Recent Deals',
                ...listItems(source.slice(0, 5).map((d) => ({ primary: dealLabel(d), metric: dealAmount(d) }))),
            };
        }
        case WIDGETS.DEALS_CLOSING_SOON:
            return {
                id: widgetId,
                kind: 'list',
                title: 'Deals Closing Soon',
                ...listItems(getDealsClosingSoon(deals).map((d) => ({ primary: dealLabel(d), secondary: d.expectedCloseDate || '' }))),
            };
        case WIDGETS.DEALS_REQUIRING_ATTENTION:
            return {
                id: widgetId,
                kind: 'list',
                title: 'Deals Requiring Attention',
                ...listItems(getDealsRequiringAttention(deals).map((d) => ({ primary: dealLabel(d), secondary: d.expectedCloseDate || '' }))),
            };
        case WIDGETS.MAJOR_OPPORTUNITIES: {
            const major = pipeline.filter((d) => M.dealAmount(d) > 50000).sort((a, b) => M.dealAmount(b) - M.dealAmount(a));
            return {
                id: widgetId,
                kind: 'list',
                title: 'Major Opportunities',
                ...listItems(major.slice(0, 5).map((d) => ({ primary: dealLabel(d), metric: dealAmount(d) }))),
            };
        }
        case WIDGETS.TOP_CUSTOMERS: {
            const customers = won.reduce((acc, d) => {
                const name = d.clientName || d.accountName;
                if (!name) return acc;
                acc[name] = (acc[name] || 0) + M.dealAmount(d);
                return acc;
            }, {});
            const sorted = Object.entries(customers)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5);
            return {
                id: widgetId,
                kind: 'list',
                title: 'Top Customers',
                ...listItems(sorted.map(([name, revenue]) => ({
                    primary: name,
                    metric: money(revenue),
                }))),
            };
        }
        case WIDGETS.NEW_CUSTOMERS:
            return {
                id: widgetId,
                kind: 'list',
                title: 'New Customers',
                ...listItems(won.slice(0, 5).map((d) => ({
                    primary: d.accountName || d.clientName || dealLabel(d),
                    metric: dealAmount(d),
                }))),
            };
        case WIDGETS.MY_ACCOUNTS:
        case WIDGETS.ASSIGNED_CUSTOMERS:
        case WIDGETS.RECENT_ACCOUNTS: {
            const names = uniqueAccountNames(deals).slice(0, 5);
            return {
                id: widgetId,
                kind: 'list',
                title: widgetId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                ...listItems(names.map((name) => ({ primary: name }))),
            };
        }
        case WIDGETS.NEW_ACCOUNTS:
            return {
                id: widgetId,
                kind: 'list',
                title: 'New Accounts',
                ...listItems(deals.slice(0, 5).map((d) => ({
                    primary: d.accountName || d.clientName || dealLabel(d),
                    metric: dealAmount(d),
                }))),
            };
        case WIDGETS.CRM_SUMMARY:
            return {
                id: widgetId,
                kind: 'summary',
                title: 'CRM Summary',
                stats: {
                    'Total deals': deals.length,
                    'Open deals': pipeline.length,
                    'Won deals': won.length,
                    Revenue: money(metrics.actualInPeriod),
                },
            };
        case WIDGETS.PIPELINE_OVERVIEW:
            return {
                id: widgetId,
                kind: 'summary',
                title: 'Pipeline Overview',
                stats: {
                    'Pipeline value': money(pipelineTotal),
                    'Open deals': pipeline.length,
                    'Avg deal size': money(pipeline.length > 0 ? pipelineTotal / pipeline.length : 0),
                },
            };
        case WIDGETS.BUSINESS_HEALTH:
            return {
                id: widgetId,
                kind: 'summary',
                title: 'Business Health',
                stats: {
                    Revenue: money(metrics.actualInPeriod),
                    Pipeline: money(pipelineTotal),
                    Won: won.length,
                    'At risk': goalMetrics.atRiskCount || 0,
                },
            };
        case WIDGETS.SALES_PERFORMANCE:
            return {
                id: widgetId,
                kind: 'summary',
                title: 'Sales Performance',
                stats: {
                    'Total deals': deals.length,
                    Won: won.length,
                    Revenue: money(metrics.actualInPeriod),
                    Conversion: `${M.winRate(deals)}%`,
                },
            };
        case WIDGETS.TODAYS_TASKS: {
            const today = M.todayKey();
            const due = M.tasksDueOn(activities || [], today).slice(0, 5);
            return {
                id: widgetId,
                kind: 'list',
                title: "Today's Tasks",
                ...listItems(due.map((a) => ({ primary: a.subject || a.activity_type || 'Task', secondary: M.activityDueDate(a) }))),
            };
        }
        case WIDGETS.UPCOMING_MEETINGS:
        case WIDGETS.TODAYS_CALLS:
        case WIDGETS.MY_CONTACTS:
        case WIDGETS.USER_ACTIVITY:
        case WIDGETS.DEPARTMENT_PERFORMANCE:
            return { id: widgetId, kind: 'list', title: widgetId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), type: 'empty', text: 'Nothing to display' };
        case WIDGETS.PENDING_FOLLOW_UPS: {
            const pending = (deals || []).filter((d) => d.status === 'open' && d.nextStepDate).slice(0, 5);
            return {
                id: widgetId,
                kind: 'list',
                title: 'Pending Follow Ups',
                ...listItems(pending.map((d) => ({ primary: dealLabel(d), metric: dealAmount(d) }))),
            };
        }
        case WIDGETS.RECENT_ACTIVITIES:
        case WIDGETS.RECENT_INTERACTIONS:
            return {
                id: widgetId,
                kind: 'list',
                title: widgetId === WIDGETS.RECENT_INTERACTIONS ? 'Recent Interactions' : 'Recent Activities',
                ...listItems(deals.slice(0, 5).map((d) => ({
                    primary: dealLabel(d),
                    secondary: `${d.updatedByName || 'Unknown'} · ${d.updatedAtDisplay || 'Recently'}`,
                }))),
            };
        case WIDGETS.RENEWALS:
        case WIDGETS.CUSTOMER_HEALTH:
            return {
                id: widgetId,
                kind: 'list',
                title: widgetId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                ...listItems(deals.slice(0, 5).map((d) => ({ primary: dealLabel(d), metric: dealAmount(d) }))),
            };
        default:
            return null;
    }
}
